export type RaidLevel = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '01' | '10' | '50' | '60';
export type DiskStatus = 'online' | 'failed' | 'rebuilding';
export type DiskMedia = 'hdd-7200' | 'sata-ssd' | 'nvme-ssd';

export const DISK_MEDIA_PROFILES: Record<DiskMedia, {
  label: string;
  shortLabel: string;
  readMbps: number;
  writeMbps: number;
  latencyMs: number;
}> = {
  'hdd-7200': { label: '7,200 RPM HDD', shortLabel: 'HDD', readMbps: 210, writeMbps: 190, latencyMs: 8.5 },
  'sata-ssd': { label: 'SATA SSD', shortLabel: 'SATA SSD', readMbps: 550, writeMbps: 520, latencyMs: 0.08 },
  'nvme-ssd': { label: 'NVMe SSD', shortLabel: 'NVMe', readMbps: 3500, writeMbps: 3000, latencyMs: 0.025 },
};

export interface VirtualDisk {
  id: number;
  name: string;
  sizeGb: number;
  status: DiskStatus;
  media: DiskMedia;
  usedGb: number;
  rebuildProgress: number;
}

export const RAID_LEVELS: Record<RaidLevel, {
  label: string;
  minimum: number;
  tolerance: number;
  description: string;
  readBoost: number;
  writePenalty: number;
}> = {
  '0': { label: 'RAID 0', minimum: 2, tolerance: 0, description: 'Striping for maximum speed. No redundancy.', readBoost: 1, writePenalty: 1 },
  '1': { label: 'RAID 1', minimum: 2, tolerance: 1, description: 'Mirroring keeps an identical copy on every disk. It remains online while any one member survives.', readBoost: 0.8, writePenalty: 2 },
  '2': { label: 'RAID 2', minimum: 3, tolerance: 1, description: 'Historical bit-level striping with Hamming-code error correction.', readBoost: 0.7, writePenalty: 3 },
  '3': { label: 'RAID 3', minimum: 3, tolerance: 1, description: 'Byte-level striping with one dedicated parity disk.', readBoost: 0.75, writePenalty: 4 },
  '4': { label: 'RAID 4', minimum: 3, tolerance: 1, description: 'Block-level striping with a dedicated parity disk.', readBoost: 0.8, writePenalty: 4 },
  '5': { label: 'RAID 5', minimum: 3, tolerance: 1, description: 'Distributed single parity balances capacity and resilience.', readBoost: 0.85, writePenalty: 4 },
  '6': { label: 'RAID 6', minimum: 4, tolerance: 2, description: 'Dual distributed parity survives any two disk failures.', readBoost: 0.8, writePenalty: 6 },
  '01': { label: 'RAID 0+1', minimum: 4, tolerance: 1, description: 'A mirror of two stripe sets. Less failure-isolated than RAID 10.', readBoost: 0.9, writePenalty: 2 },
  '10': { label: 'RAID 10', minimum: 4, tolerance: 1, description: 'A stripe across mirrored pairs. It can survive one failure in every pair.', readBoost: 0.9, writePenalty: 2 },
  '50': { label: 'RAID 50', minimum: 6, tolerance: 2, description: 'A stripe across two RAID 5 groups. Each group can lose one disk.', readBoost: 0.9, writePenalty: 4 },
  '60': { label: 'RAID 60', minimum: 8, tolerance: 4, description: 'A stripe across two RAID 6 groups. Each group can lose two disks.', readBoost: 0.85, writePenalty: 6 },
};

export const MAX_VIRTUAL_DISKS = 16;

export function requiresBalancedGroups(level: RaidLevel): boolean {
  return ['01', '10', '50', '60'].includes(level);
}

export function isValidDiskCount(level: RaidLevel, count: number): boolean {
  return count >= RAID_LEVELS[level].minimum
    && count <= MAX_VIRTUAL_DISKS
    && (!requiresBalancedGroups(level) || count % 2 === 0);
}

export function normalizedDiskCount(level: RaidLevel, currentCount: number): number {
  let count = Math.max(RAID_LEVELS[level].minimum, Math.min(MAX_VIRTUAL_DISKS, currentCount));
  if (requiresBalancedGroups(level) && count % 2 !== 0) count += 1;
  return Math.min(MAX_VIRTUAL_DISKS, count);
}

export function arrayMediaLimits(disks: VirtualDisk[]): { readMbps: number; writeMbps: number; latencyMs: number } {
  if (!disks.length) return { readMbps: 0, writeMbps: 0, latencyMs: 0 };
  return disks.reduce((limits, disk) => {
    const profile = DISK_MEDIA_PROFILES[disk.media];
    return {
      readMbps: Math.min(limits.readMbps, profile.readMbps),
      writeMbps: Math.min(limits.writeMbps, profile.writeMbps),
      latencyMs: Math.max(limits.latencyMs, profile.latencyMs),
    };
  }, { readMbps: Number.POSITIVE_INFINITY, writeMbps: Number.POSITIVE_INFINITY, latencyMs: 0 });
}

export function usableCapacity(level: RaidLevel, disks: VirtualDisk[]): number {
  if (!disks.length) return 0;
  const smallest = Math.min(...disks.map((disk) => disk.sizeGb));
  switch (level) {
    case '0': return smallest * disks.length;
    case '1': return smallest;
    case '2': return smallest * Math.max(1, disks.length - Math.ceil(Math.log2(disks.length + 1)));
    case '3':
    case '4':
    case '5': return smallest * Math.max(0, disks.length - 1);
    case '6': return smallest * Math.max(0, disks.length - 2);
    case '01':
    case '10': return smallest * Math.floor(disks.length / 2);
    case '50': return smallest * Math.max(0, disks.length - 2);
    case '60': return smallest * Math.max(0, disks.length - 4);
  }
}

export function arrayHealth(level: RaidLevel, disks: VirtualDisk[]): 'healthy' | 'degraded' | 'failed' {
  const failed = disks.filter((disk) => disk.status !== 'online').length;
  if (!isValidDiskCount(level, disks.length)) return 'failed';
  if (failed === 0) return 'healthy';
  if (level === '1') return failed < disks.length ? 'degraded' : 'failed';
  if (level === '01') {
    const half = disks.length / 2;
    const leftFailed = disks.slice(0, half).some((disk) => disk.status !== 'online');
    const rightFailed = disks.slice(half).some((disk) => disk.status !== 'online');
    return leftFailed && rightFailed ? 'failed' : 'degraded';
  }
  if (level === '10') {
    for (let i = 0; i < disks.length; i += 2) {
      if (disks[i]?.status !== 'online' && disks[i + 1]?.status !== 'online') return 'failed';
    }
    return 'degraded';
  }
  if (level === '50' || level === '60') {
    const half = disks.length / 2;
    const allowedPerGroup = level === '50' ? 1 : 2;
    const leftFailed = disks.slice(0, half).filter((disk) => disk.status !== 'online').length;
    const rightFailed = disks.slice(half).filter((disk) => disk.status !== 'online').length;
    return leftFailed > allowedPerGroup || rightFailed > allowedPerGroup ? 'failed' : 'degraded';
  }
  return failed <= RAID_LEVELS[level].tolerance ? 'degraded' : 'failed';
}

export function formatCapacity(gb: number): string {
  if (gb >= 1000) return `${(gb / 1000).toFixed(gb % 1000 === 0 ? 0 : 1)} TB`;
  if (gb < 1) return `${Math.round(gb * 1024)} MB`;
  return `${gb.toFixed(gb < 10 ? 1 : 0)} GB`;
}

export function placementForStripe(level: RaidLevel, diskCount: number, stripe: number): Array<'data' | 'parity' | 'parity2' | 'mirror' | 'empty'> {
  const cells: Array<'data' | 'parity' | 'parity2' | 'mirror' | 'empty'> = Array(diskCount).fill('data');
  if (level === '1') return cells.map((_, index) => index === 0 ? 'data' : 'mirror');
  if (level === '2') {
    const parityCount = Math.min(diskCount - 1, Math.ceil(Math.log2(diskCount + 1)));
    return cells.map((_, index) => index >= diskCount - parityCount ? 'parity' : 'data');
  }
  if (level === '3' || level === '4') cells[diskCount - 1] = 'parity';
  if (level === '01') return cells.map((_, index) => index < diskCount / 2 ? 'data' : 'mirror');
  if (level === '10') return cells.map((_, index) => index % 2 === 0 ? 'data' : 'mirror');
  if (level === '5') cells[(diskCount - 1 - stripe % diskCount + diskCount) % diskCount] = 'parity';
  if (level === '6') {
    cells[(diskCount - 1 - stripe % diskCount + diskCount) % diskCount] = 'parity';
    cells[(diskCount - 2 - stripe % diskCount + diskCount) % diskCount] = 'parity2';
  }
  if (level === '50' || level === '60') {
    const half = diskCount / 2;
    for (let groupStart = 0; groupStart < diskCount; groupStart += half) {
      const parityIndex = groupStart + (half - 1 - stripe % half + half) % half;
      cells[parityIndex] = 'parity';
      if (level === '60') cells[groupStart + (half - 2 - stripe % half + half) % half] = 'parity2';
    }
  }
  return cells;
}
